import React, { useState } from 'react';
import { Button, Menu, MenuItem, ListItemText, Box, useMediaQuery } from '@mui/material';
import cx from 'classnames';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

import styles from './SectionSelector.module.scss';

// Below this width, collapse the button to icon-only to save header space.
const COMPACT_MAX_WIDTH = '(max-width:1299.95px)';

interface SectionSelectorProps {
  sections: Array<{
    id: string;
    label: string;
    path?: string;
  }>;
  onSectionSelect: (id: string) => void;
}

export const SectionSelector: React.FC<SectionSelectorProps> = ({ sections, onSectionSelect }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const history = useHistory();
  const isCompact = useMediaQuery(COMPACT_MAX_WIDTH);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSectionClick = (section: { id: string; path?: string }) => {
    handleClose();

    if (section.path) {
      history.push(section.path);
    } else {
      // Defer the scroll until the menu has fully closed: scrolling in the same
      // frame as the close lets the Menu's transition interrupt the smooth scroll.
      setPendingId(section.id);
    }
  };

  return (
    <Box>
      <Button
        onClick={handleClick}
        startIcon={isCompact ? undefined : <MenuIcon />}
        className={cx(styles.jumpToSectionButton, { [styles.compact]: isCompact })}
        aria-label="Jump to Section"
      >
        {isCompact ? <MenuIcon /> : 'Jump to Section'}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        TransitionProps={{
          onExited: () => {
            if (pendingId) {
              onSectionSelect(pendingId);
              setPendingId(null);
            }
          },
        }}
      >
        {sections.map((section) => (
          <MenuItem
            key={section.id}
            onClick={() => handleSectionClick(section)}
            sx={{
              padding: '12px 16px',
              '&:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
          >
            <ListItemText
              primary={section.label}
              primaryTypographyProps={{ fontSize: '14px', color: '#333' }}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
